import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '../../../../components/ui/button';
import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Input } from '../../../../components/ui/input';
import { FolderResponse, folderService } from '../../services/folderApi';
import blogService, { allBlogResponse } from '../../services/blogApi';
import { toast } from 'react-hot-toast';
import { BlogContext } from '../../BlogContext';
import CheckAuthModel from './CheckAuthModel';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Plus } from 'lucide-react';

export const extractImagesFromMarkdown = (markdownContent: string): string => {
  const imageRegex = /!\[.*?\]\((.*?)\)/g;
  const urls: string[] = [];
  let match;

  while ((match = imageRegex.exec(markdownContent)) !== null) {
    if (match[1]) {
      urls.push(match[1]);
    }
  }

  return urls.join(',');
};

function BlogSaveAs() {
  const { blogContent, isGenerating } = useContext(BlogContext);
  const [open, setOpen] = useState<boolean>(false);
  const [folders, setFolders] = useState<FolderResponse[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [blogName, setBlogName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [allBlogs, setAllBlogs] = useState<allBlogResponse[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  // Get blog_id from URL if it exists (for editing existing blogs)
  const searchParams = new URLSearchParams(location.search);
  const currentBlogId = Number(searchParams.get('id'));

  useEffect(() => {
    if (open) {
      fetchFolders();
      fetchAllBlogs();
      // Set initial blog name from title if available
      if (blogContent?.title) {
        setBlogName(blogContent.title);
      }
    }
  }, [open, blogContent?.title]);

  const fetchFolders = async () => {
    try {
      const response = await folderService.getAllFolders();
      if (response.success && response.data) {
        setFolders(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch folders:', error);
      toast.error('Failed to load folders');
    }
  };

  const fetchAllBlogs = async () => {
    try {
      const response = await blogService.getAllBlogs();
      if (response.success && response.data) {
        setAllBlogs(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
      toast.error('Failed to load blogs');
    }
  };

  const handleCreateFolder = async (
    e?: React.KeyboardEvent | React.MouseEvent
  ) => {
    // Only check for key if it's a keyboard event
    if (e && 'key' in e && e.key !== 'Enter') return;

    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      const response = await folderService.createFolder(newFolderName);
      if (response.success && response.data) {
        toast.success('Folder created successfully');
        setFolders([...folders, response.data]);
        setSelectedFolder(String(response.data.id));
        setShowNewFolderInput(false);
        setNewFolderName('');
      } else {
        toast.error('Failed to create folder');
      }
    } catch (error) {
      console.error('Failed to create folder:', error);
      toast.error('Failed to create folder');
    }
  };

  // Check if blog with same name exists in the selected folder
  const checkBlogExists = () => {
    const folderId = Number(selectedFolder);
    const existingBlog = allBlogs.find(
      (blog) =>
        blog.folder_id === folderId &&
        blog.title.toLowerCase() === blogName.toLowerCase() &&
        blog.id !== currentBlogId // Exclude current blog when editing
    );

    return existingBlog;
  };

  const handleSave = async () => {
    if (!blogName.trim()) {
      toast.error('Please enter a blog name');
      return;
    }
    if (!selectedFolder) {
      toast.error('Please select a folder');
      return;
    }

    if (!blogContent) {
      toast.error('No blog content to save');
      return;
    }

    // Check if blog with same name exists in the selected folder
    const existingBlog = checkBlogExists();
    if (existingBlog) {
      toast.error(
        'A blog with this name already exists in the selected folder'
      );
      return;
    }

    setIsLoading(true);
    try {
      const markDownContent = blogContent.markDownContent;
      if (!markDownContent) {
        throw new Error('No content to save');
      }

      // Extract image URLs from markdown content
      const imageUrls = extractImagesFromMarkdown(markDownContent);
      const response = await blogService.saveBlog({
        title: blogName,
        folder_id: Number(selectedFolder),
        context: markDownContent,
        file_name: String(blogName),
        images_urls: imageUrls,
        min_len: String(blogContent.settings.minLength),
        max_len: String(blogContent.settings.maxLength),
        language: String(blogContent.settings.language),
        tone: String(blogContent.settings.toneOfVoice),
        industry: String(blogContent.settings.industry),
        no_of_images: String(blogContent.settings.imageCount),
        topic: blogName,
      });

      if (response.success) {
        toast.success('Blog saved successfully');
        setOpen(false);
        // Reset form
        setBlogName('');
        setSelectedFolder('');
        // Navigate to history
        navigate('/ai-blog-writer/history');
      } else {
        toast.error('Failed to save blog');
      }
    } catch (error) {
      console.error('Failed to save blog:', error);
      toast.error('Failed to save blog');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setBlogName('');
    setSelectedFolder('');
    setShowNewFolderInput(false);
    setNewFolderName('');
  };

  return (
    <>
      <Button
        variant="customSecondary"
        size="sm"
        className="text-[17px] bg-secondary w-full"
        disabled={isGenerating}
        onClick={() => {
          if (isAuthenticated) {
            setOpen(true);
          } else {
            setShowAuthModal(true);
          }
        }}
      >
        <div className="flex lg:hidden">Edit / </div>
        Save
      </Button>

      {/* Auth Modal for non-logged in users */}
      {showAuthModal && (
        <CheckAuthModel setVisibleEditModel={setShowAuthModal} />
      )}

      {/* Save As Modal for logged in users */}
      {isAuthenticated && (
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[9999]" />
            <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-background rounded-lg shadow-lg p-6 w-[400px] lg:w-[28vw] max-w-md flex flex-col gap-1 z-[10000]">
              <Dialog.Title className="text-2xl text-left font-bold text-text-primary">
                Save as
                <div className="lg:hidden text-[13px] text-[red]">
                  {' '}
                  If you want to edit the blog please save here and open it in
                  desktop{' '}
                </div>
              </Dialog.Title>
              <div className="flex flex-col mt-2 w-full gap-4">
                <div className="flex flex-col">
                  <label className="flex flex-col text-text-primary font-normal text-left">
                    Blog name
                    <Input
                      className="rounded-md mt-2"
                      value={blogName}
                      onChange={(e) => setBlogName(e.target.value)}
                      placeholder="Enter blog name"
                    />
                  </label>
                </div>
                <div className="flex flex-col">
                  <label className="flex flex-col text-text-primary font-normal text-left">
                    Folder
                    {showNewFolderInput ? (
                      <div className="mt-2 relative">
                        <Input
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          placeholder="Enter new folder name"
                          className="pr-16"
                          onKeyDown={handleCreateFolder}
                          autoFocus
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 text-xs">
                          <span
                            className="text-primary cursor-pointer hover:underline"
                            onClick={handleCreateFolder}
                          >
                            Save
                          </span>
                          <span className="text-gray-400">|</span>
                          <span
                            className="text-gray-500 cursor-pointer hover:underline"
                            onClick={() => {
                              setShowNewFolderInput(false);
                              setNewFolderName('');
                            }}
                          >
                            Cancel
                          </span>
                        </div>
                      </div>
                    ) : (
                      <Select
                        value={selectedFolder}
                        onValueChange={setSelectedFolder}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select a folder" />
                        </SelectTrigger>
                        <SelectContent className="z-[10001]">
                          {folders.length === 0 && (
                            <SelectItem key={null} value="root">
                              root
                            </SelectItem>
                          )}
                          {folders.map((folder) => (
                            <SelectItem
                              key={folder.id}
                              value={String(folder.id)}
                            >
                              {folder.folder_name}
                            </SelectItem>
                          ))}
                          <div
                            className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:underline text-primary"
                            onClick={() => setShowNewFolderInput(true)}
                          >
                            <Plus size={16} />
                            <span>Create new folder</span>
                          </div>
                        </SelectContent>
                      </Select>
                    )}
                  </label>
                </div>
              </div>
              <div className="flex flex-row gap-4 mt-6 items-center justify-end max-w-lg">
                <Button
                  variant="customSecondary"
                  size="sm"
                  onClick={handleClose}
                  className="text-[14px] bg-secondary"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="customSecondary"
                  size="sm"
                  onClick={handleSave}
                  className="text-[14px] bg-secondary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </>
  );
}

export default BlogSaveAs;
